import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {SCROLL_DIRECTION_DOWN} from './constants';
import {getActivePageId} from './activePage';

interface ScrollPosition {
    topOffset: number;
    leftOffset: number;
}

interface ScrollDimensions {
    container: {
        width: number;
        height: number;
    };
    content: {
        width: number;
        height: number;
    };
}

type ScrollDirection = 'UP' | 'DOWN';

interface ScrollState {
    position: ScrollPosition;
    scrollDirection: ScrollDirection;
    dimensions: ScrollDimensions;
    zoomFactor: number;
    isScrolling: boolean;
    lastScrollTimestamp: number;
}

const initialState: ScrollState = {
    position: {
        topOffset: 0,
        leftOffset: 0
    },
    scrollDirection: SCROLL_DIRECTION_DOWN,
    dimensions: {
        container: {
            width: 0,
            height: 0
        },
        content: {
            width: 0,
            height: 0
        }
    },
    zoomFactor: 1,
    isScrolling: false,
    lastScrollTimestamp: 0
};

const scrollSlice = createSlice({
    name: 'scroll',
    initialState,
    reducers: {
        updateScrollPosition: (state, action: PayloadAction<{topOffset: number; leftOffset: number; timestamp?: number}>) => {
            const {topOffset, leftOffset, timestamp = Date.now()} = action.payload;

            state.position.topOffset = topOffset;
            state.position.leftOffset = leftOffset;
            state.lastScrollTimestamp = timestamp;
            state.isScrolling = true;
        },

        updateScrollDirection: (state, action: PayloadAction<{scrollDirection: ScrollDirection}>) => {
            const {scrollDirection} = action.payload;
            state.scrollDirection = scrollDirection;
        },

        updateScrollDimensions: (state, action: PayloadAction<{container?: {width: number; height: number}; content?: {width: number; height: number}}>) => {
            const {container, content} = action.payload;

            if (container) {
                state.dimensions.container = {
                    width: container.width || 0,
                    height: container.height || 0
                };
            }

            if (content) {
                state.dimensions.content = {
                    width: content.width || 0,
                    height: content.height || 0
                };
            }
        },

        updateZoomFactor: (state, action: PayloadAction<{zoomFactor: number}>) => {
            const {zoomFactor} = action.payload;
            state.zoomFactor = zoomFactor;
        },

        setScrollingStopped: (state) => {
            state.isScrolling = false;
        },

        resetScrollState: (state) => {
            state.position.topOffset = 0;
            state.position.leftOffset = 0;
            state.scrollDirection = SCROLL_DIRECTION_DOWN;
            state.isScrolling = false;
            state.lastScrollTimestamp = 0;
        },

        updateScrollState: (state, action: PayloadAction<{
            topOffset?: number;
            leftOffset?: number;
            scrollDirection?: ScrollDirection;
            dimensions?: {container?: {width: number; height: number}; content?: {width: number; height: number}};
            zoomFactor?: number;
            timestamp?: number;
        }>) => {
            const {
                topOffset,
                leftOffset,
                scrollDirection,
                dimensions,
                zoomFactor,
                timestamp = Date.now()
            } = action.payload;

            if (topOffset !== undefined) {
                state.position.topOffset = topOffset;
            }

            if (leftOffset !== undefined) {
                state.position.leftOffset = leftOffset;
            }

            if (scrollDirection !== undefined) {
                state.scrollDirection = scrollDirection;
            }

            if (dimensions) {
                if (dimensions.container) {
                    state.dimensions.container = dimensions.container;
                }
                if (dimensions.content) {
                    state.dimensions.content = dimensions.content;
                }
            }

            if (zoomFactor !== undefined) {
                state.zoomFactor = zoomFactor;
            }

            state.lastScrollTimestamp = timestamp;
            state.isScrolling = true;
        }
    }
});

export const {
    updateScrollPosition,
    updateScrollDirection,
    updateScrollDimensions,
    updateZoomFactor,
    setScrollingStopped,
    resetScrollState,
    updateScrollState
} = scrollSlice.actions;

export const selectScrollPosition = (state: {scroll: ScrollState}) => state.scroll.position;
export const selectTopOffset = (state: {scroll: ScrollState}) => state.scroll.position.topOffset;
export const selectLeftOffset = (state: {scroll: ScrollState}) => state.scroll.position.leftOffset;
export const selectScrollDirection = (state: {scroll: ScrollState}) => state.scroll.scrollDirection;
export const selectScrollDimensions = (state: {scroll: ScrollState}) => state.scroll.dimensions;
export const selectZoomFactor = (state: {scroll: ScrollState}) => state.scroll.zoomFactor;
export const selectIsScrolling = (state: {scroll: ScrollState}) => state.scroll.isScrolling;

/**
 * Returns a selector that calculates current page index based on scroll position
 * Uses page boundaries for accurate calculation instead of average height
 */
export const selectCurrentPageIndex = (pagesWithDimensions: any[] = []) => (state: {scroll: ScrollState}): number => {
    const {topOffset} = state.scroll.position;

    if (pagesWithDimensions.length === 0) return 0;

    const currentPageIndex = pagesWithDimensions.findIndex(
        ({topBoundary, bottomBoundary}) => topOffset >= Math.floor(topBoundary) && topOffset < bottomBoundary
    );

    return currentPageIndex >= 0 ? currentPageIndex : Math.max(0, pagesWithDimensions.length - 1);
};

/**
 * Returns a selector that calculates current page index with coarse granularity
 * Updates only when crossing page boundaries, reducing re-renders
 */
export const selectCurrentPageIndexCoarse = (pagesWithDimensions: any[] = []) => (state: {scroll: ScrollState}): number => {
    const {topOffset} = state.scroll.position;

    if (pagesWithDimensions.length === 0) return 0;

    const coarseTopOffset = Math.round(topOffset / 50) * 50;

    const currentPageIndex = pagesWithDimensions.findIndex(
        ({topBoundary, bottomBoundary}) => coarseTopOffset >= Math.floor(topBoundary) && coarseTopOffset < bottomBoundary
    );

    return currentPageIndex >= 0 ? currentPageIndex : Math.max(0, pagesWithDimensions.length - 1);
};

/**
 * Returns a primitive pageId so subscribers re-render per page change, not per scroll tick.
 */
export const selectActivePageId = (pagesWithDimensions: any[] = []) => (state: {scroll: ScrollState}): string | undefined => {
    const {topOffset} = state.scroll.position;

    return getActivePageId({topOffset, pagesWithDimensions});
};

export default scrollSlice.reducer;
